/* ==========================================================================
   Hidecraft — quick-view.js
   Opens any [data-quick-view="<product url>"] link in a modal. The product is
   rendered with the product.quick-view template (layout: false), so the
   modal reuses the full variant picker / add to cart from main-product.
   ========================================================================== */

(() => {
  'use strict';

  const { lockScroll } = window.Hidecraft;

  function loadAssets(doc) {
    const pending = [];
    doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (href && !document.querySelector(`link[href="${href}"]`)) {
        const el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = href;
        pending.push(new Promise((resolve) => {
          el.onload = el.onerror = resolve;
        }));
        document.head.appendChild(el);
      }
    });
    doc.querySelectorAll('script[src]').forEach((script) => {
      const src = script.getAttribute('src');
      if (src && !document.querySelector(`script[src="${src}"]`)) {
        const el = document.createElement('script');
        el.src = src;
        el.defer = true;
        pending.push(new Promise((resolve) => {
          el.onload = el.onerror = resolve;
        }));
        document.head.appendChild(el);
      }
    });
    return Promise.all(pending);
  }

  class QuickViewModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-quick-view-content]');
      this.cache = new Map();

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-quick-view]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(trigger.dataset.quickView, trigger);
      });

      this.querySelector('[data-quick-view-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.onClose());

      // Close once the product is added; the cart drawer / notification takes over.
      document.addEventListener('cart:item-added', () => {
        if (this.dialog.open) this.dialog.close();
      });
    }

    async open(url, opener) {
      this.opener = opener;
      this.content.innerHTML = '<div class="quick-view__loading" role="status"><span class="button is-loading" aria-hidden="true"></span></div>';
      this.dialog.showModal();
      lockScroll(true);

      const viewUrl = `${url}${url.includes('?') ? '&' : '?'}view=quick-view`;
      try {
        let html = this.cache.get(viewUrl);
        if (!html) {
          const res = await fetch(viewUrl);
          if (!res.ok) throw new Error(res.status);
          html = await res.text();
          this.cache.set(viewUrl, html);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const info = doc.querySelector('product-info');
        if (!info) throw new Error('No product-info');

        await loadAssets(doc);
        if (!this.dialog.open) return;
        this.content.replaceChildren(document.adoptNode(info));
        this.content.querySelector('#QuickViewTitle a, input, button')?.focus();
      } catch (error) {
        console.warn(error);
        window.location.href = url;
      }
    }

    onClose() {
      lockScroll(false);
      this.content.innerHTML = '';
      if (this.opener?.isConnected) this.opener.focus();
    }
  }

  customElements.define('quick-view-modal', QuickViewModal);
})();
